import { useCallback, useEffect, useState } from 'react';
import { Fingerprint } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { biometricAvailable, biometricSaved, saveBiometric, forgetBiometric } from '../../utils/biometric';

// Phone, Settings → Profile: switch fingerprint / face sign-in on or off.
// Turning it on asks for your password once (to check it and keep it in the phone's secure vault).
// Renders nothing on the website or on a phone without a fingerprint / face set up.
export const BiometricToggle = () => {
  const [state, setState] = useState(null);       // null = checking, 'none' = unavailable, 'on', 'off'
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!(await biometricAvailable())) { setState('none'); return; }
    setState((await biometricSaved()) ? 'on' : 'off');
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!state || state === 'none') return null;
  const on = state === 'on';

  const turnOff = async () => {
    setBusy(true);
    await forgetBiometric();
    toast('Biometric sign-in is off');
    setBusy(false);
    refresh();
  };

  const turnOn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const email = user?.email;
      if (!email) throw new Error('Please sign in again first');
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error('That password is not right');
      await saveBiometric(email, password);
      toast.success('Biometric sign-in is on');
      setAskPassword(false); setPassword('');
    } catch (err) {
      toast.error(err?.message || 'Could not turn on biometric sign-in');
    }
    setBusy(false);
    refresh();
  };

  return (
    <div className="m-pushtoggle">
      <div className="m-pt-row">
        <span className="m-pt-icon"><Fingerprint size={22} /></span>
        <span className="m-pt-text">
          <span className="m-pt-title">Fingerprint / face sign-in</span>
          <span className="m-pt-sub">{on ? 'On. Use your fingerprint instead of typing your password' : 'Off. Turn on to skip typing your email and password'}</span>
        </span>
        <button
          type="button" role="switch" aria-checked={on} aria-label="Biometric sign-in"
          className={`m-switch${on ? ' is-on' : ''}`} disabled={busy}
          onClick={() => (on ? turnOff() : setAskPassword(a => !a))}
        ><span className="m-switch-knob" /></button>
      </div>
      {!on && askPassword && (
        <form onSubmit={turnOn} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
          <input className="form-input" type="password" autoComplete="current-password" placeholder="Enter your password to confirm" value={password} onChange={e => setPassword(e.target.value)} required />
          <button className="btn btn-primary" type="submit" disabled={busy || !password} style={{ justifyContent: 'center' }}>{busy ? 'Please wait...' : 'Turn on'}</button>
        </form>
      )}
    </div>
  );
};
